<!--
  烂尾单分析（原型：订单 - 订单跟进 - 烂尾单分析）

  ★ 数据权限跟随「项目（订单）」：本表没有自己的 crm_permission，
  列表 / 导出 / 详情由后端按归属订单的负责人过滤。
-->
<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="90px"
    >
      <el-form-item label="关键字" prop="keyword">
        <el-input
          v-model="queryParams.keyword"
          class="!w-260px"
          clearable
          placeholder="项目名称/客户名称/单号/原因/申请人"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="客户" prop="customerId">
        <el-select
          v-model="queryParams.customerId"
          class="!w-200px"
          clearable
          filterable
          placeholder="请选择客户"
        >
          <el-option
            v-for="item in customerOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="原因" prop="reasonId">
        <el-select
          v-model="queryParams.reasonId"
          class="!w-200px"
          clearable
          filterable
          placeholder="请选择原因"
        >
          <el-option
            v-for="item in reasonOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="项目状态" prop="orderStatus">
        <el-select
          v-model="queryParams.orderStatus"
          class="!w-160px"
          clearable
          placeholder="全部状态"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.CRM_ORDER_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['crm:order-stall-analysis:create']"
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['crm:order-stall-analysis:import']"
          plain
          type="warning"
          @click="openImportForm"
        >
          <Icon class="mr-5px" icon="ep:upload" />
          导入
        </el-button>
        <el-button
          v-hasPermi="['crm:order-stall-analysis:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column align="center" fixed="left" label="客户" prop="customerName" width="180" />
      <el-table-column align="center" label="项目" prop="projectName" width="200" />
      <el-table-column align="center" label="联系人" prop="contactName" width="120" />
      <el-table-column align="center" label="原因" prop="reasonName" width="180" />
      <el-table-column align="center" label="原因详细说明" prop="reasonDetail" width="280" />
      <el-table-column align="center" label="解决方案" prop="solution" width="280" />
      <el-table-column align="center" label="申请人" prop="creatorName" width="110" />
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="120"
      >
        <template #default="scope">
          <div class="crm-order-followup-action">
            <el-button
              v-hasPermi="['crm:order-stall-analysis:update']"
              link
              type="primary"
              @click="openForm('update', scope.row.id)"
            >
              编辑
            </el-button>
            <el-button
              v-hasPermi="['crm:order-stall-analysis:delete']"
              link
              type="danger"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <StallAnalysisForm ref="formRef" @success="getList" />
  <!-- 导入弹窗 -->
  <StallAnalysisImportForm ref="importFormRef" @success="getList" />
</template>

<script lang="ts" setup>
import download from '@/utils/download'
import { OrderStallAnalysisApi, OrderStallAnalysisVO } from '@/api/crm/orderStallAnalysis'
import { OrderStallReasonApi } from '@/api/crm/orderStallReason'
import * as CustomerApi from '@/api/crm/customer'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import StallAnalysisForm from './StallAnalysisForm.vue'
import StallAnalysisImportForm from './StallAnalysisImportForm.vue'

defineOptions({ name: 'CrmOrderStallAnalysis' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref<OrderStallAnalysisVO[]>([]) // 列表的数据
const customerOptions = ref<any[]>([]) // 客户下拉
const reasonOptions = ref<any[]>([]) // 原因下拉（烂尾单原因分类）
const exportLoading = ref(false) // 导出的加载中

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 10,
  keyword: '',
  customerId: undefined,
  reasonId: undefined,
  orderStatus: undefined
})
const queryFormRef = ref() // 搜索的表单

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await OrderStallAnalysisApi.getOrderStallAnalysisPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.keyword = ''
  queryParams.customerId = undefined
  queryParams.reasonId = undefined
  queryParams.orderStatus = undefined
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref<InstanceType<typeof StallAnalysisForm>>()
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

/** 导入操作 */
const importFormRef = ref<InstanceType<typeof StallAnalysisImportForm>>()
const openImportForm = () => {
  importFormRef.value?.open()
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await OrderStallAnalysisApi.deleteOrderStallAnalysis(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await OrderStallAnalysisApi.exportOrderStallAnalysis(queryParams)
    download.excel(data, '烂尾单分析.xlsx')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 初始化 **/
onMounted(async () => {
  customerOptions.value = await CustomerApi.getCustomerSimpleList()
  reasonOptions.value = await OrderStallReasonApi.getOrderStallReasonListAll()
  await getList()
})
</script>

<style lang="scss" scoped>
.crm-order-followup-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}
</style>
